import { FC } from 'react'
import { RetroWindow, RetroButton } from '@/components/retro'

interface TradeConfirmModalProps {
  isOpen: boolean
  tradeType: 'buy' | 'sell' | 'swap'
  fromSymbol: string
  toSymbol: string
  amount: number
  estimatedOutput: number
  portfolioValue: number
  portfolioPercent: number
  onConfirm: () => void
  onCancel: () => void
}

export const TradeConfirmModal: FC<TradeConfirmModalProps> = ({
  isOpen,
  tradeType,
  fromSymbol,
  toSymbol,
  amount,
  estimatedOutput,
  portfolioValue,
  portfolioPercent,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null

  const isLargeTrade = portfolioPercent >= 50
  const isYoloTrade = portfolioPercent >= 90

  const getWarningText = () => {
    if (isYoloTrade) return 'YOLO TRADE DETECTED'
    if (isLargeTrade) return 'LARGE TRADE WARNING'
    return 'CONFIRM TRADE'
  }

  const formatAmount = (value: number, symbol: string) => {
    if (symbol === 'USDC') return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    if (value < 1) return value.toFixed(6)
    if (value < 100) return value.toFixed(4)
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <RetroWindow
        title={getWarningText()}
        className="max-w-sm w-full"
        onClose={onCancel}
      >
        <div className="space-y-4">
          {/* Warning Banner */}
          {isLargeTrade && (
            <div className={`p-2 border-2 ${isYoloTrade ? 'border-game-loss bg-game-loss/20' : 'border-yellow-400 bg-yellow-400/20'}`}>
              <p className={`font-pixel text-xs ${isYoloTrade ? 'text-game-loss' : 'text-yellow-400'} text-center`}>
                {isYoloTrade
                  ? '⚠️ This trade is 90%+ of your portfolio! ⚠️'
                  : `⚠️ This trade is ${portfolioPercent.toFixed(0)}% of your portfolio`
                }
              </p>
            </div>
          )}

          {/* Trade Details */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-retro text-sm text-game-muted">Type:</span>
              <span className={`font-pixel text-sm ${
                tradeType === 'buy' ? 'text-game-profit' :
                tradeType === 'sell' ? 'text-game-loss' :
                'text-game-accent'
              }`}>
                {tradeType.toUpperCase()}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="font-retro text-sm text-game-muted">Spending:</span>
              <span className="font-mono text-sm text-game-text">
                {formatAmount(amount, fromSymbol)} {fromSymbol}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="font-retro text-sm text-game-muted">Receiving (est.):</span>
              <span className="font-mono text-sm text-game-accent">
                {formatAmount(estimatedOutput, toSymbol)} {toSymbol}
              </span>
            </div>

            <div className="border-t border-game-border pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="font-retro text-sm text-game-muted">Portfolio Value:</span>
                <span className="font-mono text-sm text-game-text">
                  ${portfolioValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-retro text-sm text-game-muted">Trade Size:</span>
                <span className={`font-mono text-sm font-bold ${
                  isYoloTrade ? 'text-game-loss' :
                  isLargeTrade ? 'text-yellow-400' :
                  'text-game-text'
                }`}>
                  {portfolioPercent.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Degen Quote */}
          <p className="font-retro text-xs text-game-muted italic text-center border-t border-game-border pt-2">
            {isYoloTrade
              ? '"Few understand. But are you sure you understand?"'
              : isLargeTrade
                ? '"Size matters. Make sure you can handle this."'
                : '"Let\'s get this bread."'
            }
          </p>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <RetroButton
              variant="default"
              className="flex-1"
              onClick={onCancel}
            >
              CANCEL
            </RetroButton>
            <RetroButton
              variant={isYoloTrade ? 'primary' : 'primary'}
              className={`flex-1 ${isYoloTrade ? 'bg-game-loss hover:bg-game-loss/80' : ''}`}
              onClick={onConfirm}
            >
              {isYoloTrade ? 'YOLO IT' : 'CONFIRM'}
            </RetroButton>
          </div>
        </div>
      </RetroWindow>
    </div>
  )
}
